/**
 * Uplate - knjiga, red za ručnu proveru i uvoz izvoda.
 *
 * Ručni unos mora da postoji **bez obzira** na automatiku: neko plati na ruke,
 * neko uplati pogrešan iznos, negde se pogreši pa se ispravlja. Automatika te
 * slučajeve ne rešava - ona skida sa čoveka onih 90 % urednih uplata.
 *
 * Uplata se **ne menja nego se dodaje**: storno je nov red u minusu, a ne
 * brisanje. Zato ekran nigde ne nudi „obriši".
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, FileUp, Inbox, Plus, RefreshCw, RotateCcw, ShieldAlert, X } from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Raspored = { billingInvoiceId: string; invoiceNumber: string | null; amount: string; note: string | null };

type Uplata = {
  id: string; tenantId: string | null; tenantName: string | null;
  amount: string; valueDate: string; source: 'manual' | 'statement' | 'online';
  reference: string | null; payerName: string | null; note: string | null;
  reversalOfPaymentId: string | null; createdAt: string;
  allocations: Raspored[];
};

type ZaProveru = Uplata & { allocatedRsd: string; unallocatedRsd: string; reason: string };
type Kandidat = { tenantId: string; tenantName: string; overdueRsd: string; daysLate: number; invoices: string[] };
type Klijent = { id: string; name: string };

const IZVOR: Record<string, string> = { manual: 'ručno', statement: 'izvod', online: 'onlajn' };
const dinar = (v: string) => `${Number(v).toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD`;

export function PaymentsPage() {
  const [tab, setTab] = useState<'knjiga' | 'provera' | 'suspenzija'>('knjiga');
  const [uplate, setUplate] = useState<Uplata[]>([]);
  const [zaProveru, setZaProveru] = useState<ZaProveru[]>([]);
  const [kandidati, setKandidati] = useState<Kandidat[]>([]);
  const [klijenti, setKlijenti] = useState<Klijent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [unos, setUnos] = useState(false);
  const [uvoz, setUvoz] = useState(false);
  const [storno, setStorno] = useState<Uplata | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [k, p, s, t] = await Promise.all([
        api.get<Uplata[]>('/admin/payments'),
        api.get<ZaProveru[]>('/admin/payments/unmatched'),
        api.get<Kandidat[]>('/admin/payments/suspension-candidates'),
        api.get<any>('/admin/tenants'),
      ]);
      setUplate(k); setZaProveru(p); setKandidati(s);
      setKlijenti(Array.isArray(t) ? t : (t.rows ?? []));
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div>
      <header style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Uplate</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Knjiga se <strong>dopunjava, ne menja</strong> - ispravka je nov red, pa se uvek vidi šta je bilo pre
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => void refresh()} disabled={loading} style={btnSecondary}><RefreshCw size={13} /> Osveži</button>
        <button onClick={() => setUvoz(true)} style={btnSecondary} data-testid="uvoz-izvoda"><FileUp size={13} /> Uvezi izvod</button>
        <button onClick={() => setUnos(true)} style={btnPrimary} data-testid="nova-uplata"><Plus size={13} /> Unesi uplatu</button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {poruka && <div style={okBox}><Check size={14} /> {poruka}</div>}

      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        <Tab aktivan={tab === 'knjiga'} onClick={() => setTab('knjiga')}>Knjiga uplata ({uplate.length})</Tab>
        <Tab aktivan={tab === 'provera'} onClick={() => setTab('provera')} istaknuto={zaProveru.length > 0}>
          Za ručnu proveru ({zaProveru.length})
        </Tab>
        <Tab aktivan={tab === 'suspenzija'} onClick={() => setTab('suspenzija')} istaknuto={kandidati.length > 0}>
          Predlog za suspenziju ({kandidati.length})
        </Tab>
      </div>

      {tab === 'knjiga' && <Knjiga uplate={uplate} onStorno={setStorno} />}
      {tab === 'provera' && <RedZaProveru redovi={zaProveru} />}
      {tab === 'suspenzija' && <Suspenzije kandidati={kandidati} />}

      {unos && (
        <UnosUplate
          klijenti={klijenti}
          onZatvori={() => setUnos(false)}
          onGotovo={(p) => { setUnos(false); setPoruka(p); void refresh(); }}
          onGreska={setError}
        />
      )}
      {uvoz && (
        <UvozIzvoda
          onZatvori={() => setUvoz(false)}
          onGotovo={(p) => { setUvoz(false); setPoruka(p); void refresh(); }}
          onGreska={setError}
        />
      )}
      {storno && (
        <StornoDijalog
          uplata={storno}
          onZatvori={() => setStorno(null)}
          onGotovo={() => { setStorno(null); setPoruka('Uplata stornirana - original ostaje u knjizi.'); void refresh(); }}
          onGreska={setError}
        />
      )}
    </div>
  );
}

function Knjiga({ uplate, onStorno }: { uplate: Uplata[]; onStorno: (u: Uplata) => void }) {
  if (uplate.length === 0) {
    return <div style={{ ...card, padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
      <Inbox size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} /><div>Još nema nijedne uplate.</div>
    </div>;
  }
  return (
    <div style={card}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }} data-testid="uplate-tabela">
        <thead>
          <tr style={{ background: 'var(--color-bg)' }}>
            <th style={{ ...th, width: 100 }}>Valuta</th>
            <th style={{ ...th, width: 130, textAlign: 'right' }}>Iznos</th>
            <th style={{ ...th, width: 80 }}>Izvor</th>
            <th style={th}>Klijent</th>
            <th style={th}>Poziv na broj</th>
            <th style={th}>Raspored</th>
            <th style={{ ...th, width: 70 }} />
          </tr>
        </thead>
        <tbody>
          {uplate.map((u) => {
            const minus = Number(u.amount) < 0;
            return (
              <tr key={u.id} style={minus ? { opacity: 0.7 } : undefined}>
                <td style={{ ...td, fontSize: 12 }}>{String(u.valueDate).slice(0, 10)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: minus ? '#fca5a5' : 'var(--color-text-strong)' }} className="tabular-nums">
                  {dinar(u.amount)}
                </td>
                <td style={{ ...td, fontSize: 12 }}>{IZVOR[u.source] ?? u.source}</td>
                <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.tenantName ?? <span style={{ color: 'var(--color-text-muted)' }}>nepoznat</span>}
                </td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.reference ?? '-'}
                </td>
                <td style={{ ...td, fontSize: 12 }}>
                  {u.allocations.length === 0
                    ? <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                    : u.allocations.map((a) => `${a.invoiceNumber ?? '?'}: ${Number(a.amount).toLocaleString('sr-RS')}`).join(' · ')}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  {!minus && !u.reversalOfPaymentId && (
                    <button onClick={() => onStorno(u)} title="Storniraj" style={iconBtn} data-testid={`storno-${u.id}`}>
                      <RotateCcw size={13} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RedZaProveru({ redovi }: { redovi: ZaProveru[] }) {
  if (redovi.length === 0) {
    return <div style={{ ...card, padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
      <Check size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
      <div>Nema uplata koje čekaju čoveka.</div>
    </div>;
  }
  return (
    <>
      <div style={napomena}>
        Automat sme da <strong>predloži</strong>, ne da presudi. Ovde stoji sve u šta nije bio siguran - bolje da
        čovek pogleda deset uplata nego da jedna zatvori pogrešan račun.
      </div>
      <div style={{ ...card, marginTop: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ ...th, width: 100 }}>Valuta</th>
              <th style={{ ...th, width: 130, textAlign: 'right' }}>Iznos</th>
              <th style={{ ...th, width: 130, textAlign: 'right' }}>Neraspoređeno</th>
              <th style={th}>Uplatilac</th>
              <th style={th}>Zašto čeka</th>
            </tr>
          </thead>
          <tbody>
            {redovi.map((u) => (
              <tr key={u.id}>
                <td style={{ ...td, fontSize: 12 }}>{String(u.valueDate).slice(0, 10)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }} className="tabular-nums">{dinar(u.amount)}</td>
                <td style={{ ...td, textAlign: 'right', color: '#fcd34d' }} className="tabular-nums">{dinar(u.unallocatedRsd)}</td>
                <td style={td}>{u.payerName ?? '-'}</td>
                <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{u.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Suspenzije({ kandidati }: { kandidati: Kandidat[] }) {
  if (kandidati.length === 0) {
    return <div style={{ ...card, padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
      <Check size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
      <div>Niko nije u kašnjenju preko grejs perioda.</div>
    </div>;
  }
  return (
    <>
      <div style={{ ...napomena, background: 'rgba(245,158,11,0.12)', color: '#fcd34d' }}>
        <ShieldAlert size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
        Ovaj spisak <strong>ništa ne menja</strong>. Suspenzija se pokreće ručno, sa stranice klijenta - bag u
        uparivanju koji suspenduje klijenta koji je platio košta više od nedelju dana kašnjenja u naplati.
      </div>
      <div style={{ ...card, marginTop: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={th}>Klijent</th>
              <th style={{ ...th, width: 140, textAlign: 'right' }}>Duguje</th>
              <th style={{ ...th, width: 100, textAlign: 'right' }}>Kasni</th>
              <th style={th}>Računi</th>
            </tr>
          </thead>
          <tbody>
            {kandidati.map((k) => (
              <tr key={k.tenantId}>
                <td style={{ ...td, fontWeight: 600, color: 'var(--color-text-strong)' }}>{k.tenantName}</td>
                <td style={{ ...td, textAlign: 'right', color: '#fca5a5', fontWeight: 600 }} className="tabular-nums">{dinar(k.overdueRsd)}</td>
                <td style={{ ...td, textAlign: 'right' }} className="tabular-nums">{k.daysLate} dana</td>
                <td style={{ ...td, fontSize: 12, color: 'var(--color-text-muted)' }}>{k.invoices.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UnosUplate({ klijenti, onZatvori, onGotovo, onGreska }: {
  klijenti: Klijent[]; onZatvori: () => void; onGotovo: (p: string) => void; onGreska: (e: string) => void;
}) {
  const [amountRsd, setAmount] = useState('');
  const [valueDate, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [tenantId, setTenant] = useState('');
  const [reference, setRef] = useState('');
  const [payerName, setPayer] = useState('');
  const [note, setNote] = useState('');
  const [radi, setRadi] = useState(false);

  const sacuvaj = async () => {
    setRadi(true);
    try {
      const r = await api.post<{ outcome: { kind: string } | null; closedInvoiceIds: string[] }>('/admin/payments', {
        amountRsd, valueDate, tenantId: tenantId || undefined, reference: reference || undefined,
        payerName: payerName || undefined, note: note || undefined,
      });
      const zatvoreno = r.closedInvoiceIds.length;
      onGotovo(zatvoreno > 0
        ? `Uplata proknjižena - zatvoreno računa: ${zatvoreno}.`
        : 'Uplata proknjižena.');
    } catch (e: any) {
      onGreska(e?.data?.message || e?.message || 'Uplata nije proknjižena');
    } finally { setRadi(false); }
  };

  return (
    <Dijalog naslov="Unos uplate" onZatvori={onZatvori}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Polje label="Iznos (RSD)"><input value={amountRsd} onChange={(e) => setAmount(e.target.value)} placeholder="5274.19" style={input} data-testid="uplata-iznos" /></Polje>
        <Polje label="Datum valute"><input type="date" value={valueDate} onChange={(e) => setDate(e.target.value)} style={input} /></Polje>
      </div>
      <Polje label="Klijent (opciono - inače se traži iz poziva na broj)">
        <select value={tenantId} onChange={(e) => setTenant(e.target.value)} style={input} data-testid="uplata-klijent">
          <option value="">Prepoznaj iz poziva na broj</option>
          {klijenti.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </Polje>
      <Polje label="Poziv na broj"><input value={reference} onChange={(e) => setRef(e.target.value)} placeholder="97116011971-000191" style={{ ...input, fontFamily: 'monospace' }} /></Polje>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Polje label="Uplatilac"><input value={payerName} onChange={(e) => setPayer(e.target.value)} style={input} /></Polje>
        <Polje label="Napomena"><input value={note} onChange={(e) => setNote(e.target.value)} style={input} /></Polje>
      </div>

      <div style={napomena}>
        Iznos se raspoređuje <strong>od najstarijeg dospeća</strong>. Ono što pretekne ostaje kao pretplata - to
        nije greška nego novac koji čeka sledeći račun.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        <button onClick={onZatvori} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
        <button onClick={() => void sacuvaj()} disabled={radi || !amountRsd || !valueDate} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: radi || !amountRsd ? 0.5 : 1 }} data-testid="uplata-sacuvaj">
          {radi ? 'Čuvanje...' : 'Sačuvaj'}
        </button>
      </div>
    </Dijalog>
  );
}

function UvozIzvoda({ onZatvori, onGotovo, onGreska }: {
  onZatvori: () => void; onGotovo: (p: string) => void; onGreska: (e: string) => void;
}) {
  const [csv, setCsv] = useState('');
  const [radi, setRadi] = useState(false);
  const [skipped, setSkipped] = useState<Array<{ line: number; reason: string }>>([]);

  const posalji = async () => {
    setRadi(true); setSkipped([]);
    try {
      const r = await api.post<{ rows: number; imported: number; duplicates: number; needsReview: number; closedInvoices: number; skipped: any[] }>(
        '/admin/payments/import', { csv },
      );
      setSkipped(r.skipped ?? []);
      onGotovo(
        `Uvezeno ${r.imported} od ${r.rows} · duplikata ${r.duplicates} · za proveru ${r.needsReview} · zatvoreno računa ${r.closedInvoices}`,
      );
    } catch (e: any) {
      setSkipped(e?.data?.skipped ?? []);
      onGreska(e?.data?.message || e?.message || 'Izvod nije uvezen');
    } finally { setRadi(false); }
  };

  const izFajla = async (f: File | null) => { if (f) setCsv(await f.text()); };

  return (
    <Dijalog naslov="Uvoz izvoda" onZatvori={onZatvori}>
      <Polje label="CSV fajl">
        <input type="file" accept=".csv,text/csv" onChange={(e) => void izFajla(e.target.files?.[0] ?? null)} style={{ ...input, padding: 6 }} />
      </Polje>
      <Polje label="…ili nalepi sadržaj">
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} style={{ ...input, fontFamily: 'monospace', fontSize: 11 }} data-testid="izvod-csv" />
      </Polje>

      <div style={napomena}>
        Uvoze se <strong>samo prilivi</strong>. Isti izvod uvezen dvaput ne knjiži novac ponovo - to je zaštita, ne
        greška. Redovi koji ne prođu se prijavljuju, ne nestaju tiho.
      </div>

      {skipped.length > 0 && (
        <div style={{ ...napomena, marginTop: 8, background: 'rgba(245,158,11,0.12)', color: '#fcd34d' }}>
          {skipped.slice(0, 5).map((s) => <div key={s.line}>red {s.line}: {s.reason}</div>)}
          {skipped.length > 5 && <div>…i još {skipped.length - 5}</div>}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        <button onClick={onZatvori} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
        <button onClick={() => void posalji()} disabled={radi || csv.trim().length === 0} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: radi || !csv.trim() ? 0.5 : 1 }} data-testid="izvod-uvezi">
          {radi ? 'Čuvanje...' : 'Sačuvaj'}
        </button>
      </div>
    </Dijalog>
  );
}

function StornoDijalog({ uplata, onZatvori, onGotovo, onGreska }: {
  uplata: Uplata; onZatvori: () => void; onGotovo: () => void; onGreska: (e: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [radi, setRadi] = useState(false);

  const posalji = async () => {
    setRadi(true);
    try {
      await api.post(`/admin/payments/${uplata.id}/reverse`, { reason });
      onGotovo();
    } catch (e: any) {
      onGreska(e?.data?.message || e?.message || 'Storno nije prošao');
    } finally { setRadi(false); }
  };

  return (
    <Dijalog naslov={`Storno - ${dinar(uplata.amount)}`} onZatvori={onZatvori}>
      <div style={napomena}>
        Original <strong>ostaje u knjizi</strong>; storno je nov red u minusu. Tako se vidi da je uplata stigla i
        zašto je poništena - brisanje bi uklonilo baš taj trag.
      </div>
      <div style={{ marginTop: 12 }}>
        <Polje label="Razlog (obavezno)">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="npr. uplata je bila tuđa" style={input} data-testid="storno-razlog" />
        </Polje>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        <button onClick={onZatvori} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
        <button onClick={() => void posalji()} disabled={radi || !reason.trim()} style={{ ...btnPrimary, background: '#dc2626', width: '100%', justifyContent: 'center', opacity: radi || !reason.trim() ? 0.5 : 1 }} data-testid="storno-potvrdi">
          Storniraj
        </button>
      </div>
    </Dijalog>
  );
}

function Tab({ aktivan, istaknuto, onClick, children }: { aktivan: boolean; istaknuto?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
      border: '1px solid ' + (aktivan ? 'var(--color-primary, #2563eb)' : 'var(--color-border)'),
      background: aktivan ? 'rgba(37,99,235,0.15)' : 'transparent',
      color: aktivan ? '#93c5fd' : (istaknuto ? '#fcd34d' : 'var(--color-text-muted)'),
    }}>{children}</button>
  );
}

function Polje({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

function Dijalog({ naslov, children, onZatvori }: { naslov: string; children: React.ReactNode; onZatvori: () => void }) {
  useEscapeToClose(onZatvori);
  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>{naslov}</h3>
          <button onClick={onZatvori} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' }}><X size={16} /></button>
        </header>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', overflow: 'hidden' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, background: 'var(--color-primary, #2563eb)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, borderRadius: 4, cursor: 'pointer' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, outline: 'none' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 14 };
const okBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 14 };
const napomena: React.CSSProperties = { padding: 10, borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text-muted)', fontSize: 12, lineHeight: 1.5 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 560, background: 'var(--color-bg-surface)', borderRadius: 12, border: '1px solid var(--color-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', maxHeight: '85vh', overflow: 'auto' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' };
